import React from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatCurrencyAmount } from "@/components/pages/services/utils/priceFormatting";
import type {
  PartnerCuratedItemSource,
  PartnerDestinationProduct,
} from "@/types/partner-destination";

const sourceBadgeVariant = (
  source: PartnerCuratedItemSource,
): "default" | "outline" | "secondary" => {
  if (source === "ai_top") return "default";
  if (source === "ai_score") return "outline";
  return "secondary";
};

interface DestinationProductCardProps {
  product: PartnerDestinationProduct;
  action: React.ReactNode;
  isDragging?: boolean;
  className?: string;
}

export const DestinationProductCard: React.FC<DestinationProductCardProps> = ({
  product,
  action,
  isDragging,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      className={cn(
        "flex-row items-center gap-3 p-3",
        isDragging && "shadow-lg",
        className,
      )}
    >
      <div className="bg-muted size-16 shrink-0 overflow-hidden rounded-md">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.title}
            className="size-full object-cover"
          />
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-foreground truncate text-sm font-medium">
          {product.title}
        </p>

        {product.source && (
          <div className="flex items-center gap-2">
            {product.aiReason ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant={sourceBadgeVariant(product.source)}
                    className="w-fit"
                  >
                    {t(
                      `partners.admin.destinations.sourceValues.${product.source}`,
                    )}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {t("partners.admin.destinations.detail.aiReasonLabel", {
                    reason: product.aiReason,
                  })}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Badge
                variant={sourceBadgeVariant(product.source)}
                className="w-fit"
              >
                {t(
                  `partners.admin.destinations.sourceValues.${product.source}`,
                )}
              </Badge>
            )}
            {product.aiScore != null && (
              <span className="text-muted-foreground text-xs">
                {t("partners.admin.destinations.detail.aiScoreLabel", {
                  score: product.aiScore,
                })}
              </span>
            )}
          </div>
        )}

        <div className="text-muted-foreground flex items-center gap-3 text-xs">
          {product.rating != null ? (
            <span className="flex items-center gap-1">
              <Star className="size-3 fill-current" />
              {t("partners.admin.destinations.detail.rating", {
                rating: product.rating.toFixed(1),
                count: product.reviewCount ?? 0,
              })}
            </span>
          ) : (
            <span>{t("partners.admin.destinations.detail.noRating")}</span>
          )}

          {product.price != null && (
            <span title={t("partners.admin.destinations.detail.netPriceHint")}>
              {formatCurrencyAmount(product.price, product.currency)}{" "}
              <span className="italic">
                ({t("partners.admin.destinations.detail.netPrice")})
              </span>
            </span>
          )}
        </div>

        {product.productUrl && (
          <a
            href={product.productUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="text-primary flex w-fit items-center gap-1 text-xs hover:underline"
          >
            <ExternalLink className="size-3" />
            {t("partners.admin.destinations.detail.viewOnViator")}
          </a>
        )}
      </div>

      <div className="shrink-0">{action}</div>
    </Card>
  );
};
